import * as Sentry from '@sentry/react';
import type { StorageUnit } from 'store/storageunits/types';
import { StorageUnitType } from 'store/storageunits/types';
import { hasOfflineFileData, normalizeLeases } from 'store/storageunits/utils';
import { getCurrentUserId } from 'auth/userIdentity';

const DB_NAME = 'SandvikPlantDesigner';
const DB_VERSION = 3;
const STORAGE_UNITS_STORE = 'storageUnits';
const LISTS_STORE = 'storageLists';
const SYNC_QUEUE_STORE = 'storageSyncQueue';
const MAX_CACHED_FILES = 50;
const ANONYMOUS_OWNER_ID = '__anonymous__';

interface CachedStorageUnit {
  cacheKey: string;
  ownerId: string;
  id: string;
  data: StorageUnit;
  timestamp: number;
  lastAccessed: number;
}

interface CachedStorageUnitsList {
  cacheKey: string;
  ownerId: string;
  query: string;
  data: StorageUnit[];
  timestamp: number;
}

export type PendingAction = 'create' | 'update' | 'archive';

interface SyncQueueEntry {
  cacheKey: string;
  ownerId: string;
  id: string;
  pendingAction: PendingAction;
  syncedAt?: string | null;
  localUpdatedAt: string;
}

class StorageUnitCacheIndexedDB {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;

  private getOwnerId(): string {
    return getCurrentUserId() ?? ANONYMOUS_OWNER_ID;
  }

  private getStorageCacheKey(id: string, ownerId = this.getOwnerId()): string {
    return `${ownerId}::${id}`;
  }

  private getListCacheKey(query: string, ownerId = this.getOwnerId()): string {
    return `${ownerId}::${query}`;
  }

  private createStorageUnitsStore(db: IDBDatabase) {
    const storageUnitsStore = db.createObjectStore(STORAGE_UNITS_STORE, {
      keyPath: 'cacheKey',
    });
    storageUnitsStore.createIndex('ownerId', 'ownerId');
    storageUnitsStore.createIndex('lastAccessed', 'lastAccessed');
    storageUnitsStore.createIndex('timestamp', 'timestamp');
  }

  private createListsStore(db: IDBDatabase) {
    const listsStore = db.createObjectStore(LISTS_STORE, {
      keyPath: 'cacheKey',
    });
    listsStore.createIndex('ownerId', 'ownerId');
    listsStore.createIndex('timestamp', 'timestamp');
  }

  private createSyncQueueStore(db: IDBDatabase) {
    const queueStore = db.createObjectStore(SYNC_QUEUE_STORE, {
      keyPath: 'cacheKey',
    });
    queueStore.createIndex('ownerId', 'ownerId');
  }

  private async initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        const error = new Error(
          `IndexedDB error: ${request.error?.message ?? 'Unknown error'}`,
        );
        (error as Error & { cause?: unknown }).cause = request.error;
        reject(error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.db.onclose = () => {
          this.db = null;
          this.dbPromise = null;
        };
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (event.oldVersion < 3) {
          if (db.objectStoreNames.contains(STORAGE_UNITS_STORE)) {
            db.deleteObjectStore(STORAGE_UNITS_STORE);
          }
          if (db.objectStoreNames.contains(LISTS_STORE)) {
            db.deleteObjectStore(LISTS_STORE);
          }
          if (db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
            db.deleteObjectStore(SYNC_QUEUE_STORE);
          }
        }

        if (!db.objectStoreNames.contains(STORAGE_UNITS_STORE)) {
          this.createStorageUnitsStore(db);
        }

        if (!db.objectStoreNames.contains(LISTS_STORE)) {
          this.createListsStore(db);
        }

        if (!db.objectStoreNames.contains(SYNC_QUEUE_STORE)) {
          this.createSyncQueueStore(db);
        }
      };
    });
  }

  private async getDB(): Promise<IDBDatabase> {
    if (this.db) {
      return this.db;
    }
    this.dbPromise ??= this.initDB();
    return this.dbPromise;
  }

  private isValidStorageUnit(data: unknown): data is StorageUnit {
    if (!data || typeof data !== 'object') return false;
    const obj = data as Record<string, unknown>;
    if (!obj.id || typeof obj.id !== 'string') return false;
    if (!obj.name || typeof obj.name !== 'string') return false;
    if (!obj.type) return false;
    if (!obj.createdAt) return false;
    return true;
  }

  private normalizeDate(value?: Date | string | null): string | null {
    if (!value) {
      return null;
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      return value.toISOString();
    } catch (_err) {
      return new Date(value).toISOString();
    }
  }

  private normalizeParentId(value?: string | null): string | undefined {
    if (!value || value === 'root') {
      return undefined;
    }
    return value;
  }

  private requestToPromise<T>(
    request: IDBRequest<T>,
    fallback?: T,
  ): Promise<T | undefined> {
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(fallback);
    });
  }

  private normalizeStorageUnit(unit: StorageUnit): StorageUnit {
    const leaseNormalized = normalizeLeases(unit);

    return {
      ...leaseNormalized,
      isSynced: unit.isSynced ?? true,
      syncedAt:
        unit.syncedAt ?? this.normalizeDate(unit.updatedAt ?? unit.createdAt),
      localUpdatedAt: unit.localUpdatedAt ?? null,
      pendingAction: unit.pendingAction ?? null,
    };
  }

  private async getAllStorageEntries(): Promise<CachedStorageUnit[]> {
    const db = await this.getDB();
    const transaction = db.transaction([STORAGE_UNITS_STORE], 'readonly');
    const store = transaction.objectStore(STORAGE_UNITS_STORE);
    return (
      (await this.requestToPromise<CachedStorageUnit[]>(store.getAll(), [])) ??
      []
    );
  }

  private getOwnerStorageUnits(entries: CachedStorageUnit[]): StorageUnit[] {
    const ownerId = this.getOwnerId();
    return entries
      .filter((entry) => entry.ownerId === ownerId)
      .map((entry) => this.normalizeStorageUnit(entry.data));
  }

  private async upsertSyncEntry(entry: SyncQueueEntry): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      await this.requestToPromise(store.put(entry), undefined);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }

  private async removeSyncEntry(id: string): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([SYNC_QUEUE_STORE], 'readwrite');
      const store = transaction.objectStore(SYNC_QUEUE_STORE);
      const cacheKey = this.getStorageCacheKey(id);
      await this.requestToPromise(store.delete(cacheKey), undefined);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }

  async getPendingSync(): Promise<
    { entry: SyncQueueEntry; storageUnit: StorageUnit }[]
  > {
    try {
      const ownerId = this.getOwnerId();
      const db = await this.getDB();
      const transaction = db.transaction(
        [SYNC_QUEUE_STORE, STORAGE_UNITS_STORE],
        'readonly',
      );
      const queueStore = transaction.objectStore(SYNC_QUEUE_STORE);
      const storageStore = transaction.objectStore(STORAGE_UNITS_STORE);

      return await new Promise((resolve) => {
        const request = queueStore.getAll();
        request.onsuccess = async () => {
          const entries = ((request.result as SyncQueueEntry[]) ?? []).filter(
            (entry) => entry.ownerId === ownerId,
          );
          const pending: {
            entry: SyncQueueEntry;
            storageUnit: StorageUnit;
          }[] = [];
          for (const entry of entries) {
            const unitRequest = storageStore.get(entry.cacheKey);
            const unitResult: StorageUnit | undefined = await new Promise(
              (innerResolve) => {
                unitRequest.onsuccess = () => {
                  const cached: CachedStorageUnit | undefined =
                    unitRequest.result;
                  innerResolve(cached?.data);
                };
                unitRequest.onerror = () => innerResolve(undefined);
              },
            );
            if (unitResult) {
              pending.push({
                entry,
                storageUnit: unitResult,
              });
            }
          }
          resolve(pending);
        };
        request.onerror = () => resolve([]);
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      return [];
    }
  }

  async replaceTempId(
    tempId: string,
    data: StorageUnit,
  ): Promise<StorageUnit | null> {
    try {
      const tempCacheKey = this.getStorageCacheKey(tempId);
      const db = await this.getDB();
      const transaction = db.transaction(
        [STORAGE_UNITS_STORE, SYNC_QUEUE_STORE],
        'readwrite',
      );
      const storageStore = transaction.objectStore(STORAGE_UNITS_STORE);
      const syncStore = transaction.objectStore(SYNC_QUEUE_STORE);

      const existingTemp = await this.requestToPromise<CachedStorageUnit>(
        storageStore.get(tempCacheKey),
      );

      const dataWithFallback: StorageUnit = {
        ...data,
        fileData: data.fileData ?? existingTemp?.data.fileData,
        thumbnail: data.thumbnail ?? existingTemp?.data.thumbnail,
      };

      await this.requestToPromise(storageStore.delete(tempCacheKey));
      await this.requestToPromise(syncStore.delete(tempCacheKey));

      return await this.setOne(dataWithFallback, {
        markSynced: true,
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      return null;
    }
  }

  async getOne(id: string): Promise<StorageUnit | null> {
    try {
      const ownerId = this.getOwnerId();
      const cacheKey = this.getStorageCacheKey(id, ownerId);
      const db = await this.getDB();
      const transaction = db.transaction([STORAGE_UNITS_STORE], 'readwrite');
      const store = transaction.objectStore(STORAGE_UNITS_STORE);

      return new Promise((resolve, _reject) => {
        const request = store.get(cacheKey);

        request.onsuccess = () => {
          const cached: CachedStorageUnit | undefined = request.result;

          if (!cached) {
            resolve(null);
            return;
          }

          const now = Date.now();
          const normalizedData = this.normalizeStorageUnit(cached.data);

          if (!this.isValidStorageUnit(cached.data)) {
            //ToDo: log to sentry
            store.delete(cacheKey);
            resolve(null);
            return;
          }

          const updatedCache: CachedStorageUnit = {
            ...cached,
            data: normalizedData,
            ownerId,
            lastAccessed: now,
          };
          store.put(updatedCache);

          resolve(normalizedData);
        };

        request.onerror = () => {
          //ToDo: log to sentry
          resolve(null);
        };
      });
    } catch (error) {
      // NOTE: Expected in private browsing or when IndexedDB is disabled
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      return null;
    }
  }

  async setOne(
    data: StorageUnit,
    options?: {
      throwOnError?: boolean;
      markSynced?: boolean;
      pendingAction?: PendingAction | null;
      syncedAt?: string | null;
      localUpdatedAt?: string | null;
    },
  ): Promise<StorageUnit> {
    try {
      const ownerId = this.getOwnerId();
      const cacheKey = this.getStorageCacheKey(data.id, ownerId);
      const db = await this.getDB();

      const readTx = db.transaction([STORAGE_UNITS_STORE], 'readonly');
      const readStore = readTx.objectStore(STORAGE_UNITS_STORE);
      const cached = await this.requestToPromise<CachedStorageUnit>(
        readStore.get(cacheKey),
      );
      const existing = cached?.data ?? null;

      const transaction = db.transaction([STORAGE_UNITS_STORE], 'readwrite');
      const store = transaction.objectStore(STORAGE_UNITS_STORE);

      const now = Date.now();
      const nowIso = new Date(now).toISOString();
      const markSynced = options?.markSynced !== false;
      const pendingAction = markSynced
        ? null
        : (options?.pendingAction ?? data.pendingAction ?? null);

      const leaseNormalized = normalizeLeases(data);
      const mergedData: StorageUnit = {
        ...leaseNormalized,
        fileData: leaseNormalized.fileData ?? existing?.fileData,
        thumbnail: leaseNormalized.thumbnail ?? existing?.thumbnail,
      };

      const normalized: StorageUnit = {
        ...mergedData,
        isSynced: markSynced,
        pendingAction,
        syncedAt: markSynced
          ? (options?.syncedAt ??
            this.normalizeDate(data.syncedAt) ??
            this.normalizeDate(data.updatedAt ?? data.createdAt) ??
            nowIso)
          : (options?.syncedAt ??
            this.normalizeDate(data.syncedAt) ??
            existing?.syncedAt ??
            null),
        localUpdatedAt: markSynced
          ? null
          : (options?.localUpdatedAt ??
            data.localUpdatedAt ??
            existing?.localUpdatedAt ??
            nowIso),
      };

      const cachedData: CachedStorageUnit = {
        cacheKey,
        ownerId,
        id: normalized.id,
        data: normalized,
        timestamp: now,
        lastAccessed: now,
      };

      await this.requestToPromise(store.put(cachedData));

      if (normalized.isSynced === false && normalized.pendingAction) {
        await this.upsertSyncEntry({
          cacheKey,
          ownerId,
          id: normalized.id,
          pendingAction: normalized.pendingAction,
          syncedAt: normalized.syncedAt ?? null,
          localUpdatedAt: normalized.localUpdatedAt ?? nowIso,
        });
      } else {
        await this.removeSyncEntry(normalized.id);
      }

      return normalized;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      if (options?.throwOnError) throw error;
      return data;
    } finally {
      void this.cleanupOldCache();
    }
  }

  async getList(query: string): Promise<StorageUnit[] | null> {
    try {
      const ownerId = this.getOwnerId();
      const cacheKey = this.getListCacheKey(query, ownerId);
      const db = await this.getDB();
      const transaction = db.transaction([LISTS_STORE], 'readonly');
      const store = transaction.objectStore(LISTS_STORE);

      return new Promise((resolve, _reject) => {
        const request = store.get(cacheKey);

        request.onsuccess = () => {
          const cached: CachedStorageUnitsList | undefined = request.result;

          if (!cached) {
            resolve(null);
            return;
          }

          if (!Array.isArray(cached.data)) {
            //ToDO: log to sentry
            resolve(null);
            return;
          }

          const normalized = cached.data
            .map((unit) => this.normalizeStorageUnit(unit))
            .filter((unit) => !unit.archived);

          resolve(normalized);
        };

        request.onerror = () => {
          //ToDo: log to sentry
          resolve(null);
        };
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      return null;
    }
  }

  async getAllByParent(
    parentId?: string,
    type?: StorageUnitType,
  ): Promise<StorageUnit[]> {
    try {
      const requestedParent =
        parentId === undefined ? undefined : this.normalizeParentId(parentId);
      const units = this.getOwnerStorageUnits(
        await this.getAllStorageEntries(),
      );

      return units.filter((unit) => {
        if (unit.archived) {
          return false;
        }

        if (type && unit.type !== type) {
          return false;
        }

        return this.normalizeParentId(unit.parentId) === requestedParent;
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      return [];
    }
  }

  async getDownloadedByParent(parentId?: string): Promise<StorageUnit[]> {
    try {
      const requestedParent =
        parentId === undefined ? undefined : this.normalizeParentId(parentId);
      const units = this.getOwnerStorageUnits(
        await this.getAllStorageEntries(),
      );

      return units.filter((unit) => {
        if (unit.archived) {
          return false;
        }

        if (unit.type === StorageUnitType.File && !hasOfflineFileData(unit)) {
          return false;
        }

        if (requestedParent === undefined) {
          return true;
        }

        return this.normalizeParentId(unit.parentId) === requestedParent;
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      return [];
    }
  }

  async getArchived(): Promise<StorageUnit[]> {
    try {
      const units = this.getOwnerStorageUnits(
        await this.getAllStorageEntries(),
      );
      return units.filter((unit) => !!unit.archived);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
      return [];
    }
  }

  async setList(data: StorageUnit[], query: string): Promise<void> {
    try {
      const ownerId = this.getOwnerId();
      const cacheKey = this.getListCacheKey(query, ownerId);
      const db = await this.getDB();
      const transaction = db.transaction([LISTS_STORE], 'readwrite');
      const store = transaction.objectStore(LISTS_STORE);
      const normalizedData = data.map((unit) =>
        this.normalizeStorageUnit(unit),
      );

      const cachedList: CachedStorageUnitsList = {
        cacheKey,
        ownerId,
        query,
        data: normalizedData,
        timestamp: Date.now(),
      };

      await this.requestToPromise(store.put(cachedList));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }

  async updateUnitInLists(data: StorageUnit): Promise<void> {
    try {
      const ownerId = this.getOwnerId();
      const db = await this.getDB();
      const transaction = db.transaction([LISTS_STORE], 'readwrite');
      const store = transaction.objectStore(LISTS_STORE);
      const normalizedData = this.normalizeStorageUnit(data);

      await new Promise<void>((resolve) => {
        const request = store.openCursor();
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;
          if (!cursor) {
            resolve();
            return;
          }

          const cached = cursor.value as CachedStorageUnitsList;
          if (cached.ownerId !== ownerId) {
            cursor.continue();
            return;
          }

          const nextData = cached.data.map((unit) =>
            unit.id === data.id ? normalizedData : unit,
          );
          const changed = nextData.some(
            (unit, index) => unit !== cached.data[index],
          );

          if (changed) {
            const updateRequest = cursor.update({
              ...cached,
              data: nextData,
              timestamp: Date.now(),
            });
            updateRequest.onsuccess = () => cursor.continue();
            updateRequest.onerror = () => cursor.continue();
            return;
          }

          cursor.continue();
        };
        request.onerror = () => resolve();
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }

  async removeUnitFromLists(id: string): Promise<void> {
    try {
      const ownerId = this.getOwnerId();
      const db = await this.getDB();
      const transaction = db.transaction([LISTS_STORE], 'readwrite');
      const store = transaction.objectStore(LISTS_STORE);

      await new Promise<void>((resolve) => {
        const request = store.openCursor();
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;
          if (!cursor) {
            resolve();
            return;
          }

          const cached = cursor.value as CachedStorageUnitsList;
          if (cached.ownerId !== ownerId) {
            cursor.continue();
            return;
          }

          const nextData = cached.data.filter((unit) => unit.id !== id);
          if (nextData.length !== cached.data.length) {
            const updateRequest = cursor.update({
              ...cached,
              data: nextData,
              timestamp: Date.now(),
            });
            updateRequest.onsuccess = () => cursor.continue();
            updateRequest.onerror = () => cursor.continue();
            return;
          }

          cursor.continue();
        };
        request.onerror = () => resolve();
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }

  async clearOne(id: string): Promise<void> {
    try {
      const cacheKey = this.getStorageCacheKey(id);
      const db = await this.getDB();
      const transaction = db.transaction(
        [STORAGE_UNITS_STORE, SYNC_QUEUE_STORE],
        'readwrite',
      );
      const store = transaction.objectStore(STORAGE_UNITS_STORE);
      const queue = transaction.objectStore(SYNC_QUEUE_STORE);

      await this.requestToPromise(store.delete(cacheKey));
      await this.requestToPromise(queue.delete(cacheKey));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }

  async clearList(): Promise<void> {
    try {
      const ownerId = this.getOwnerId();
      const db = await this.getDB();
      const transaction = db.transaction([LISTS_STORE], 'readwrite');
      const store = transaction.objectStore(LISTS_STORE);

      await new Promise<void>((resolve) => {
        const request = store.openCursor();
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
            .result;
          if (!cursor) {
            resolve();
            return;
          }
          const cached = cursor.value as CachedStorageUnitsList;
          if (cached.ownerId === ownerId) {
            cursor.delete();
          }
          cursor.continue();
        };
        request.onerror = () => resolve();
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }

  async clearAll(): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction(
        [STORAGE_UNITS_STORE, LISTS_STORE, SYNC_QUEUE_STORE],
        'readwrite',
      );
      const storageUnitsStore = transaction.objectStore(STORAGE_UNITS_STORE);
      const listsStore = transaction.objectStore(LISTS_STORE);
      const queueStore = transaction.objectStore(SYNC_QUEUE_STORE);

      await Promise.all([
        this.requestToPromise(storageUnitsStore.clear()),
        this.requestToPromise(listsStore.clear()),
        this.requestToPromise(queueStore.clear()),
      ]);
    } catch (error) {
      //ToDo: log to sentry
    }
  }

  private async cleanupOldCache(): Promise<void> {
    try {
      const ownerId = this.getOwnerId();
      const db = await this.getDB();
      const transaction = db.transaction([STORAGE_UNITS_STORE], 'readwrite');
      const store = transaction.objectStore(STORAGE_UNITS_STORE);

      const request = store.getAll();
      await new Promise<void>((resolve) => {
        request.onsuccess = () => {
          const ownerEntries = ((request.result as CachedStorageUnit[]) ?? [])
            .filter((entry) => entry.ownerId === ownerId)
            .sort((left, right) => left.lastAccessed - right.lastAccessed);

          if (ownerEntries.length <= MAX_CACHED_FILES) {
            resolve();
            return;
          }

          let removedCount = 0;
          const toRemove = ownerEntries.length - MAX_CACHED_FILES;
          const keysToDelete: string[] = [];

          for (const entry of ownerEntries) {
            if (removedCount >= toRemove) {
              break;
            }
            if (entry.data.isSynced === false) {
              continue;
            }
            keysToDelete.push(entry.cacheKey);
            removedCount++;
          }

          if (!keysToDelete.length) {
            resolve();
            return;
          }

          let settled = 0;
          keysToDelete.forEach((key) => {
            const deleteRequest = store.delete(key);
            const onSettled = () => {
              settled++;
              if (settled === keysToDelete.length) {
                resolve();
              }
            };
            deleteRequest.onsuccess = onSettled;
            deleteRequest.onerror = onSettled;
          });
        };
        request.onerror = () => resolve();
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'storageUnitCache' },
        level: 'warning',
      });
    }
  }
}

export const storageUnitCache = new StorageUnitCacheIndexedDB();
